import { useEffect, useState } from "react";

import { getHaloClass } from "./document-intake-prefill";

const POLL_MS = 700;
const HALO_CLASSES = ["di-halo-ok", "di-halo-missing"];

/**
 * Pinta el halo de cada campo clave dentro de `containerRef` (verde: lo capturo la IA; naranja:
 * falta) y devuelve el inventario `items` ([{ label, filled }]) para el resumen.
 *
 * Los campos viven en paneles de formulario independientes, asi que se localizan por su
 * atributo `name` en el DOM en lugar de tocar cada panel. Formik fija los valores iniciales sin
 * disparar eventos, por eso ademas de escuchar input/change se revisa cada POLL_MS.
 *
 * `fields` ([{ name, label, silent }]) y `aiFields` (Set de nombres) deben ser estables entre
 * renders. Un campo `silent` no entra al inventario ni lleva halo; solo se lee. `values` trae el
 * valor actual de todos los campos encontrados, por nombre.
 */
export const useIntakeHalos = ({ containerRef, fields, aiFields, enabled }) => {
  const [items, setItems] = useState([]);
  const [values, setValues] = useState({});

  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container) {
      return undefined;
    }

    const scan = () => {
      const next = [];
      const nextValues = {};
      fields.forEach(({ name, label, silent }) => {
        const element = container.querySelector(`[name="${name}"]`);
        if (!element) {
          return;
        }
        const value = String(element.value || "");
        nextValues[name] = value;
        if (silent) {
          return;
        }
        const filled = value.trim() !== "";
        const halo = getHaloClass({ filled, fromAI: aiFields.has(name) });
        HALO_CLASSES.forEach((haloClass) => element.classList.toggle(haloClass, haloClass === halo));
        next.push({ label, filled });
      });
      setItems((previous) => (JSON.stringify(previous) === JSON.stringify(next) ? previous : next));
      setValues((previous) => (JSON.stringify(previous) === JSON.stringify(nextValues) ? previous : nextValues));
    };

    scan();
    const timer = setInterval(scan, POLL_MS);
    container.addEventListener("input", scan);
    container.addEventListener("change", scan);

    return () => {
      clearInterval(timer);
      container.removeEventListener("input", scan);
      container.removeEventListener("change", scan);
      container.querySelectorAll(HALO_CLASSES.map((c) => `.${c}`).join(",")).forEach((element) =>
        HALO_CLASSES.forEach((haloClass) => element.classList.remove(haloClass))
      );
    };
  }, [containerRef, fields, aiFields, enabled]);

  return { items, values };
};
